import { DEMO_TENANT_ID } from "@/lib/mock-auth";
import { env } from "@/lib/env";
import { authApi, type TenantMatch } from "@/features/auth/api/auth.api";

/**
 * How the login flow decides which tenant_id to submit with a password login,
 * configured via VITE_TENANT_RESOLUTION (see lib/env.ts). "hardcoded" is both
 * a selectable strategy AND the automatic fail-safe fallback within every
 * other strategy — if resolution can't determine a tenant, we fall back to
 * DEMO_TENANT_ID rather than blocking login entirely. This is a pre-production
 * safety net; remove the fallback once every tenant is reachable via a real
 * resolution strategy.
 */
export type TenantResolutionStrategy = "email-first" | "subdomain" | "field" | "hardcoded";

export function currentTenantResolutionStrategy(): TenantResolutionStrategy {
  return env.VITE_TENANT_RESOLUTION;
}

/**
 * Derives a candidate tenant slug from the current hostname for the
 * "subdomain" strategy, e.g. "makini.skul.io" -> "makini". Returns null for
 * bare/unknown hosts (localhost, IP addresses, the apex domain itself) so the
 * caller can fall back to hardcoded.
 */
export function slugFromHostname(hostname: string = window.location.hostname): string | null {
  const parts = hostname.split(".");
  // Need at least "sub.domain.tld" — bare "localhost" or "skul.io" has no
  // tenant-identifying subdomain.
  if (parts.length < 3) return null;
  const candidate = parts[0];
  if (!candidate || candidate === "www" || candidate === "app") return null;
  return candidate;
}

/**
 * Resolves a tenant_id for the subdomain strategy by calling
 * GET /auth/tenant/:slug with the slug derived from the current hostname.
 * Returns the tenant_id on success, or DEMO_TENANT_ID when:
 *   - no slug can be derived from the hostname
 *   - the API call fails (network error, 404 not found)
 */
export async function resolveTenantIdBySubdomain(): Promise<string> {
  const slug = slugFromHostname();
  if (!slug) return DEMO_TENANT_ID;
  try {
    const match = await authApi.resolveTenantBySlug(slug);
    return match.tenant_id;
  } catch {
    return DEMO_TENANT_ID;
  }
}

/**
 * Resolves a tenant_id for the field strategy by calling
 * GET /auth/tenant/:slug with the user-supplied school code.
 * Returns the TenantMatch on success, or throws with a user-facing
 * message when the slug is not found.
 *
 * Unlike other strategies this does NOT fall back to DEMO_TENANT_ID —
 * the user explicitly entered a code, so an invalid code is an error.
 */
export async function resolveTenantIdBySchoolCode(
  schoolCode: string,
): Promise<TenantMatch> {
  const match = await authApi.resolveTenantBySlug(schoolCode.trim().toLowerCase());
  return match;
}

/**
 * Resolves a tenant_id for login given the configured strategy. Each strategy
 * degrades to the hardcoded DEMO_TENANT_ID fallback if it can't determine a
 * tenant, so login always has SOME tenant_id to submit.
 *
 * - email-first: caller must already have called authApi.resolveTenant(email)
 *   and pass the resulting matches; this function just applies the
 *   single-match/no-match/multi-match decision (multi-match returns null and
 *   the caller must render a picker — see resolveTenantForEmail below).
 * - subdomain: derives a slug from the hostname and calls the slug-lookup API
 *   (GET /auth/tenant/:slug). Falls back to DEMO_TENANT_ID when no slug or
 *   no matching tenant.
 * - field: the school-code field strategy is handled by the caller reading the
 *   user's typed school code — this function falls back to DEMO_TENANT_ID since
 *   it has no school code to resolve.
 * - hardcoded: always DEMO_TENANT_ID.
 */
export function resolveTenantId(
  strategy: TenantResolutionStrategy,
  matches: TenantMatch[] | null,
): string {
  if (strategy === "email-first" && matches && matches.length === 1) {
    return matches[0].tenant_id;
  }
  return DEMO_TENANT_ID;
}
